import React, { useState, useEffect } from "react";
import styles from "./ProfileView.module.css";
import { useNavigate } from "react-router-dom";
import Layout from "../layout/Layout";
import { useLogoutMutation, User, useSaveUserProfilePictureMutation } from "../../gql/types";
import ProfilePictureUpload, { FlowType } from "./ProfilePictureUpload";
import logoutIcon from "../../assets/icons/logoutIcon.svg";
import { clearTokens } from "../../auth/jwt";
import Spinner from "../button/Spinner";
import ActionButton from "../button/ActionButton";
import composeIcon from "../../assets/icons/composeIcon.svg";
import MySettingsView from "./MySettingsView";
import useCalendarAuthRedirect from "../../hooks/useCalendarAuthRedirect";
import { Preferences } from "@capacitor/preferences";
import FamilyMemberList from "./family/FamilyMemberList";
import { fetchWithAuth } from "../../request/backend";
import { useUser } from "../../contexts/UserContext";

type ProfileViewProps = {
    user: User;
    onBack?: () => void;
    isHidden?: boolean;
};

const ProfileView: React.FC<ProfileViewProps> = ({ user, onBack, isHidden }) => {
    const { calendarAuthParams, clearCalendarAuthParams } = useCalendarAuthRedirect();
    const [showSettingView, setShowSettingView] = useState(false);

    const { setUser } = useUser();
    const navigate = useNavigate();
    const [saveProfilePicture] = useSaveUserProfilePictureMutation();

    const handleUpdateProfilePic = async (croppedImage: string | null) => {
        if (croppedImage === null) {
            return;
        }
        if (!croppedImage) {
            throw new Error('No image provided. Please upload an image.');
        }

        if (!croppedImage.startsWith('data:image/')) {
            console.error('Unexpected format detected. Input:', croppedImage);
            throw new Error('Invalid file.');
        }

        const base64Index = croppedImage.indexOf(';base64,');
        console.log("Base64 index found at:", base64Index); // Debug the index
        if (base64Index === -1) {
            console.error('Base64 marker not found in the Data URL:', croppedImage);
            throw new Error('Invalid file.');
        }

        const mimeType = croppedImage.substring(5, base64Index);
        console.log("Extracted MIME type:", mimeType); // Log extracted MIME type

        const base64Data = croppedImage.split('base64,')[1];
        if (!base64Data) {
            console.error('Base64 data missing in the Data URL:', croppedImage);
            throw new Error('Invalid file.');
        }
        console.log("Base64 data extracted successfully. Length:", base64Data.length);

        // Convert base64 to binary data
        console.log("Converting base64 data to binary data...");
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Uint8Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        console.log("Binary data conversion complete.");

        // Create Blob
        const blob = new Blob([byteNumbers], { type: mimeType });
        console.log("Blob created. Blob size:", blob.size, "MIME type:", blob.type);

        // Prepare FormData
        const formData = new FormData();
        formData.append('file', blob, 'profile-picture.jpg'); // Use a meaningful filename
        console.log("FormData prepared with file.");

        // Send the image to the backend
        console.log("Sending image to backend...");
        const response = await fetchWithAuth('/api/upload/image/profile', {
            method: 'POST',
            body: formData,
        });

        console.log("Upload response received. Status:", response.status);
        if (!response.ok) {
            const message = await response.text();
            console.error("Error response from server:", message);
            throw new Error(message || 'Upload failed');
        }

        const { url } = await response.json();

        await saveProfilePicture({
            variables: {
                profilePictureURLInput: url, // Assuming the mutation takes a `url` argument
            },
        });

        setUser((prev: User | null) => {
            if (!prev || prev === null) return prev;

            return {
                ...prev,
                profilePictureUrl: url,
            };
        });
        return url;
    };


    useEffect(() => {
        if (calendarAuthParams) {
            setShowSettingView(true);
        }
    }, [calendarAuthParams]);

    // Use the useLogoutMutation hook
    const [logout, { loading: mutationLoading }] = useLogoutMutation({
        onCompleted: async () => {
            // Clear session data and navigate to login
            await Promise.all([
                clearTokens(),         // Your custom function for clearing auth tokens
                Preferences.clear(),   // Clear Capacitor Preferences
                (async () => localStorage.clear())(), // Wrap in async IIFE to match Promise.all() format
                (async () => sessionStorage.clear())()
            ]);
            navigate('/auth/login');
        },
        onError: (error) => {
            console.error("Logout failed:", error);
        },
    });

    const handleLogout = async () => {
        try {
            await logout();
        } catch (error) {
            console.error("Error logging out:", error);
        }
    };

    const handleOpenSetting = () => {
        setShowSettingView(true);
    }

    return (
        <>
            {showSettingView && <MySettingsView onBack={() => setShowSettingView(false)} calendarAuthParams={calendarAuthParams ?? undefined} clearCalendarAuthParams={clearCalendarAuthParams} />}
            {<Layout
                navbarConfig={{ title: "My Profile", onBack, useCloseButton: false }}
                footer={(
                    <div className={styles.logoutContainer} onClick={handleLogout}>
                        <div className={styles.logOutButtonContainer}>
                            {mutationLoading
                                ? <Spinner />
                                : <img src={logoutIcon} className={styles.logoutButton} />}
                        </div>
                        <div className={styles.logOutText}>
                            Log Out
                        </div>
                    </div>
                )}
                isHidden={isHidden || showSettingView}
            >
                <div className={styles.profileViewContainer}>
                    <ProfilePictureUpload
                        onComplete={handleUpdateProfilePic}
                        flowType={FlowType.Profile}
                        initialImage={user.profilePictureUrl ?? undefined}
                    />
                    <div className={styles.nameAndEmailContainer}>
                        <div className={styles.name}>
                            {user.firstName + " " + user.lastName}
                        </div>
                        <div className={styles.email}>
                            {user.email}
                        </div>
                    </div>
                    <div className={styles.settingButtonContainer}>
                        <ActionButton
                            onClick={handleOpenSetting}
                            className={styles.settingButton}
                        >
                            My Setting <img src={composeIcon} className={styles.composeIcon} />
                        </ActionButton>
                    </div>
                    <div className={styles.sectionContainer}>
                        <div className={styles.sectionTitle}>My family</div>
                        <div className={styles.sectionDivider} />
                        {user.family && <FamilyMemberList family={user.family} />}
                    </div>
                </div>
            </Layout>
            }
        </>
    );
};

export default ProfileView;
